import { useState } from 'react';
import { useDebouncedQuery } from '~/hooks/useDebouncedQuery';
import { useListUrlState } from '~/hooks/useListUrlState';
import { useUrlParams } from '~/hooks/useUrlParams';
import { errorText } from '~/lib/apiError';
import { pageCount } from '~/lib/listParams';
import { orDash } from '~/lib/orDash';
import { queryErrorMessage } from '~/modules/query';
import { toast } from 'react-toastify';
import { Pencil, RotateCcw, Trash2 } from 'lucide-react';

import { ListToolbar, PageHeader, PageShell, type PageAction } from '~/components/layout';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardSection,
  DataTable,
  type DataTableColumn,
  type DropdownMenuItem,
  ConfirmModal,
  Pagination,
  RowActionsMenu,
  Tabs,
} from '~/components/ui';
import { EXPIRING_GROUP_TITLES } from '~/components/Constants/mlsAgreementsConstants';
import { routes } from '~/app/routes';
import { useUrlTab } from '~/hooks/useUrlTab';
import {
  useDeleteAgreementCustomerMutation,
  useDeleteAgreementMlsMutation,
  useGetAgreementExpiringQuery,
  useListCustomersQuery,
  useListMlsQuery,
  useRestoreAgreementCustomerMutation,
  useRestoreAgreementMlsMutation,
} from '~/modules/query/mlsAgreements';
import { LocalTime } from '~/components/common/LocalTime';
import {
  CUSTOMER_FILTER_KEYS,
  CustomerFilterDrawer,
  DELETED_KEY,
  MLS_FILTER_KEYS,
  MlsFilterDrawer,
} from '~/components/pages/mls-agreements/AgreementFilterDrawers';
import type {
  CustomerRow,
  ExpiringGroups,
  MembershipRow,
  MlsRow,
} from '~/components/pages/mls-agreements/types';

const SYNC_COLOR = { ok: 'success', pending: 'warning', failed: 'error' } as const;

// The lists are server-paged: page, pageSize, q and the filters go to the endpoint as they are in
// the URL. Both lists share the same URL keys, so a tab switch clears them (see `MlsAgreements`).
const LIST_KEYS = ['q', 'page', 'pageSize', 'vendor', 'dataType', 'status', 'region', DELETED_KEY];

const rowActions = ({
  href,
  deleted,
  onDelete,
  onRestore,
}: RowActionHandlers): DropdownMenuItem[] => [
  {
    label: 'Edit',
    testId: 'edit',
    icon: <Pencil className="size-4" aria-hidden="true" />,
    href,
  },
  deleted
    ? {
        label: 'Restore',
        testId: 'restore',
        icon: <RotateCcw className="size-4" aria-hidden="true" />,
        onClick: onRestore,
      }
    : {
        label: 'Delete',
        testId: 'delete',
        destructive: true,
        icon: <Trash2 className="size-4" aria-hidden="true" />,
        onClick: onDelete,
      },
];

interface RowActionHandlers {
  href: string;
  deleted: boolean;
  onDelete: () => void;
  onRestore: () => void;
}

const actionsColumn = <T extends { id?: string; deletedAt?: string | null }>(
  recordHref: (id: string) => string,
  askDelete: (row: T) => void,
  restore: (row: T) => void,
): DataTableColumn<T> => ({
  id: 'actions',
  header: 'Actions',
  cell: (r) =>
    r.id ? (
      <RowActionsMenu
        testId={`mls-agreements-actions-${r.id}`}
        items={rowActions({
          href: recordHref(r.id),
          deleted: !!r.deletedAt,
          onDelete: () => askDelete(r),
          onRestore: () => restore(r),
        })}
      />
    ) : null,
});

const DeletedBadge = () => (
  <Badge size="sm" color="gray" className="ml-2">
    Deleted
  </Badge>
);

const MlsTab = () => {
  const list = useListUrlState({ filterKeys: MLS_FILTER_KEYS });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const q = useDebouncedQuery(list.q);
  const { filters } = list;
  const { data, isLoading, isFetching, error, refetch } = useListMlsQuery({
    page: list.page,
    pageSize: list.pageSize,
    q,
    vendor: filters.vendor,
    dataType: filters.dataType,
    deleted: filters[DELETED_KEY] === 'true' ? true : undefined,
  });
  const [deleteMls] = useDeleteAgreementMlsMutation();
  const [restoreMls] = useRestoreAgreementMlsMutation();
  const [deleting, setDeleting] = useState<MlsRow | null>(null);
  const rows = data?.rows as MlsRow[] | undefined;
  const total = data?.total ?? 0;

  const remove = (row: MlsRow) =>
    deleteMls(row.id as string)
      .unwrap()
      .then(() => toast.success('MLS deleted'))
      .catch((err) =>
        toast.error(
          errorText(err, 'The delete was blocked. The record may still have active memberships.'),
        ),
      );

  const restore = (row: MlsRow) => {
    restoreMls(row.id as string)
      .unwrap()
      .then(() => toast.success('MLS restored'))
      .catch((err) => toast.error(errorText(err, 'The MLS could not be restored. Try again.')));
  };

  return (
    <>
      <Card>
        <ListToolbar
          title="MLSs"
          search={{
            value: list.q,
            onChange: list.setQ,
            placeholder: 'Search MLS, agent, brokerage…',
            testId: 'mls-agreements-mls-search',
          }}
          filters={{
            count: list.filterCount,
            onClick: () => setFiltersOpen(true),
            onClear: list.clear,
            testId: 'mls-agreements-mls-filter-button',
          }}
        />
        <CardBody className="p-0">
          <DataTable<MlsRow>
            framed={false}
            testId="mls-agreements-mls-table"
            loading={isLoading}
            refreshing={isFetching && !isLoading}
            error={
              error
                ? { what: 'the MLSs', message: queryErrorMessage(error), onRetry: refetch }
                : undefined
            }
            emptyMessage="No MLSs match."
            emptyDescription="Clear the search or filters, or create an MLS."
            data={rows ?? []}
            rowKey={(r) => r.id ?? r.name ?? ''}
            href={(r) => (r.id ? routes.mlsAgreementsMls(r.id) : null)}
            rowLabel={(r) => `Open ${r.name}`}
            columns={[
              {
                id: 'name',
                header: 'Name',
                cell: (r) => (
                  <>
                    {r.name}
                    {r.deletedAt ? <DeletedBadge /> : null}
                  </>
                ),
              },
              { id: 'status', header: 'Status', cell: (r) => orDash(r.status) },
              { id: 'region', header: 'Region', cell: (r) => orDash(r.region) },
              { id: 'dataType', header: 'Data type', cell: (r) => orDash(r.dataType) },
              { id: 'vendor', header: 'Vendor', cell: (r) => orDash(r.vendor) },
              {
                id: 'sync',
                header: 'Sync',
                cell: (r) =>
                  r.syncStatus ? (
                    <Badge
                      size="sm"
                      color={SYNC_COLOR[r.syncStatus as keyof typeof SYNC_COLOR] ?? 'gray'}
                      className="whitespace-nowrap"
                    >
                      {r.syncStatus}
                    </Badge>
                  ) : (
                    orDash(null)
                  ),
              },
              actionsColumn<MlsRow>(routes.mlsAgreementsMls, setDeleting, restore),
            ]}
          />
        </CardBody>
        <Pagination
          pageIndex={list.page - 1}
          pageSize={list.pageSize}
          pageCount={pageCount(total, list.pageSize)}
          totalRows={total}
          onPageChange={(index) => list.setPage(index + 1)}
          pageSizeControl={{
            value: list.pageSize,
            onChange: list.setPageSize,
            options: [...list.pageSizes],
          }}
        />
      </Card>

      <MlsFilterDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        value={filters}
        onApply={list.setFilters}
        onClear={list.clear}
      />

      <ConfirmModal
        isOpen={!!deleting}
        title="Delete MLS"
        message={
          <>
            Delete <strong>{deleting?.name}</strong>? It leaves the list; an admin can restore it
            with Show deleted in the filters. An MLS with active memberships can't be deleted.
          </>
        }
        confirmLabel="Delete"
        testId="mls-agreements-mls-delete"
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await remove(deleting);
          setDeleting(null);
        }}
      />
    </>
  );
};

const CustomersTab = () => {
  const list = useListUrlState({ filterKeys: CUSTOMER_FILTER_KEYS });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const q = useDebouncedQuery(list.q);
  const { filters } = list;
  const { data, isLoading, isFetching, error, refetch } = useListCustomersQuery({
    page: list.page,
    pageSize: list.pageSize,
    q,
    deleted: filters[DELETED_KEY] === 'true' ? true : undefined,
  });
  const [deleteCustomer] = useDeleteAgreementCustomerMutation();
  const [restoreCustomer] = useRestoreAgreementCustomerMutation();
  const [deleting, setDeleting] = useState<CustomerRow | null>(null);
  const rows = data?.rows as CustomerRow[] | undefined;
  const total = data?.total ?? 0;

  const remove = (row: CustomerRow) =>
    deleteCustomer(row.id as string)
      .unwrap()
      .then(() => toast.success('Customer deleted'))
      .catch((err) =>
        toast.error(
          errorText(err, 'The delete was blocked. The record may still have active memberships.'),
        ),
      );

  const restore = (row: CustomerRow) => {
    restoreCustomer(row.id as string)
      .unwrap()
      .then(() => toast.success('Customer restored'))
      .catch((err) =>
        toast.error(errorText(err, 'The customer could not be restored. Try again.')),
      );
  };

  return (
    <>
      <Card>
        <ListToolbar
          title="Customers"
          search={{
            value: list.q,
            onChange: list.setQ,
            placeholder: 'Search customer, agent, brokerage…',
            testId: 'mls-agreements-customers-search',
          }}
          filters={{
            count: list.filterCount,
            onClick: () => setFiltersOpen(true),
            onClear: list.clear,
            testId: 'mls-agreements-customers-filter-button',
          }}
        />
        <CardBody className="p-0">
          <DataTable<CustomerRow>
            framed={false}
            testId="mls-agreements-customers-table"
            loading={isLoading}
            refreshing={isFetching && !isLoading}
            error={
              error
                ? { what: 'the customers', message: queryErrorMessage(error), onRetry: refetch }
                : undefined
            }
            emptyMessage="No customers match."
            emptyDescription="Clear the search or filters, or create a customer."
            data={rows ?? []}
            rowKey={(r) => r.id ?? r.name ?? ''}
            href={(r) => (r.id ? routes.mlsAgreementsCustomer(r.id) : null)}
            rowLabel={(r) => `Open ${r.name}`}
            columns={[
              {
                id: 'name',
                header: 'Name',
                cell: (r) => (
                  <>
                    {r.name}
                    {r.deletedAt ? <DeletedBadge /> : null}
                  </>
                ),
              },
              {
                id: 'accountId',
                header: 'Account ID',
                cell: (r) =>
                  r.accountId ? (
                    r.accountId
                  ) : (
                    <span className="text-gray-500 dark:text-gray-400">unlinked</span>
                  ),
              },
              { id: 'status', header: 'Status', cell: (r) => orDash(r.status) },
              actionsColumn<CustomerRow>(routes.mlsAgreementsCustomer, setDeleting, restore),
            ]}
          />
        </CardBody>
        <Pagination
          pageIndex={list.page - 1}
          pageSize={list.pageSize}
          pageCount={pageCount(total, list.pageSize)}
          totalRows={total}
          onPageChange={(index) => list.setPage(index + 1)}
          pageSizeControl={{
            value: list.pageSize,
            onChange: list.setPageSize,
            options: [...list.pageSizes],
          }}
        />
      </Card>

      <CustomerFilterDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        value={filters}
        onApply={list.setFilters}
        onClear={list.clear}
      />

      <ConfirmModal
        isOpen={!!deleting}
        title="Delete customer"
        message={
          <>
            Delete <strong>{deleting?.name}</strong>? It leaves the list; an admin can restore it
            with Show deleted in the filters. A customer with active memberships can't be deleted.
          </>
        }
        confirmLabel="Delete"
        testId="mls-agreements-customers-delete"
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await remove(deleting);
          setDeleting(null);
        }}
      />
    </>
  );
};

const ExpiringTab = () => {
  const { data, isLoading, error, refetch } = useGetAgreementExpiringQuery();
  const groups = data as ExpiringGroups | undefined;

  return (
    <Card>
      <CardHeader title="Expiring memberships" />
      <CardBody className="space-y-6">
        {Object.entries(EXPIRING_GROUP_TITLES).map(([key, title]) => {
          const rows = groups?.[key] ?? [];
          return (
            <CardSection
              key={key}
              title={groups ? `${title} (${rows.length})` : title}
              testId={`mls-agreements-expiring-${key}`}
            >
              <DataTable<MembershipRow>
                framed={false}
                testId={`mls-agreements-expiring-${key}-table`}
                loading={isLoading}
                error={
                  error
                    ? {
                        what: 'the expiring memberships',
                        message: queryErrorMessage(error),
                        onRetry: refetch,
                      }
                    : undefined
                }
                emptyMessage="No memberships in this group."
                emptyDescription="Nothing is expiring in this window."
                data={rows}
                rowKey={(m) => m.id ?? `${m.customerId}-${m.mlsId}`}
                href={(m) => (m.id ? routes.mlsAgreementsMembership(m.id) : null)}
                rowLabel={(m) =>
                  `Open membership for ${m.customerName ?? `customer ${m.customerId}`}`
                }
                columns={[
                  {
                    id: 'customer',
                    header: 'Customer',
                    cell: (m) => m.customerName ?? `Customer #${m.customerId}`,
                  },
                  { id: 'mls', header: 'MLS', cell: (m) => m.mlsName ?? `MLS #${m.mlsId}` },
                  { id: 'product', header: 'Product', cell: (m) => orDash(m.product) },
                  { id: 'status', header: 'Status', cell: (m) => orDash(m.agreementStatus) },
                  {
                    id: 'expiration',
                    header: 'Expiration',
                    cell: (m) => <LocalTime value={m.agreementExpiration} format="date" />,
                  },
                ]}
              />
            </CardSection>
          );
        })}
      </CardBody>
    </Card>
  );
};

const TAB_IDS = ['mls', 'customers', 'expiring'] as const;
const TAB_ITEMS = [
  { id: 'mls', label: 'MLS' },
  { id: 'customers', label: 'Customers' },
  { id: 'expiring', label: 'Expiring' },
];

export const MlsAgreements = () => {
  const [tab] = useUrlTab(TAB_IDS, 'mls');
  const url = useUrlParams();

  // The whole section is gated on the MlsAgreements purview (App.tsx), so everyone here can write.
  const actions: PageAction[] =
    tab === 'mls'
      ? [
          {
            label: 'Create MLS',
            kind: 'primary',
            testId: 'mls-agreements-new-mls',
            href: routes.mlsAgreementsMlsNew,
          },
        ]
      : tab === 'customers'
        ? [
            {
              label: 'Create customer',
              kind: 'primary',
              testId: 'mls-agreements-new-customer',
              href: routes.mlsAgreementsCustomerNew,
            },
          ]
        : [];

  return (
    <PageShell>
      <PageHeader
        title="MLS agreements"
        breadcrumbs={[{ label: 'MLS agreements' }]}
        actions={actions}
      />

      <div className="border-b border-gray-200 dark:border-gray-800">
        <Tabs
          ariaLabel="MLS agreement lists"
          idPrefix="mls-agreements"
          tabs={TAB_ITEMS}
          activeId={tab}
          // Both lists share the q/page/filter keys, so a tab switch starts the next list clean.
          onChange={(id) =>
            url.set({
              ...Object.fromEntries(LIST_KEYS.map((key) => [key, null])),
              tab: id === TAB_IDS[0] ? null : id,
            })
          }
        />
      </div>

      <div
        role="tabpanel"
        id={`mls-agreements-panel-${tab}`}
        aria-labelledby={`mls-agreements-tab-${tab}`}
      >
        {tab === 'mls' && <MlsTab />}
        {tab === 'customers' && <CustomersTab />}
        {tab === 'expiring' && <ExpiringTab />}
      </div>
    </PageShell>
  );
};

export default MlsAgreements;
